import {
  CoachBasic,
  Media,
  PlayerAccount,
  PlayerSearchResponse,
  Profile,
  ConfigProperties,
  MediaCategory,
} from "../interfaces";
import { AppUserContext } from "../providers";

import { PlayerSignUpStep, signUpSteps } from "../services";
import * as IT from "../interfaces";
import { URLS } from "../utils";
import { Player } from "@/services/bedrock";

export const phoneRegExp =
  /^((\\+[1]{1}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
export const phoneNumberMask = [/[1-9]/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/];
export const webUrlRegExp = /^(https?:\/\/).*/i; // /^(http\:\/\/|https\:\/\/)?(([a-z0-9][a-z0-9\-@]*\.)+[a-z0-9][a-z0-9\-]*((([/][a-z0-9][a-z0-9\-]*)+)*)+)+([/]?)$/;
const emailRegExp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function fullNameFrom({ firstName, lastName }: PlayerSearchResponse | PlayerAccount | Profile | Player): string {
  return (firstName + " " || "") + lastName || "";
}

export function extractInitials(...args: (string | null | undefined)[]) {
  const combinedName = args
    .filter(arg => typeof arg === "string" && arg.trim() !== "")
    .join(" ")
    .trim();

  if (!combinedName) {
    return "?";
  }

  const words = combinedName.split(/\s+/);

  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  const firstInitial = words[0][0];
  const lastInitial = words[words.length - 1][0];

  return (firstInitial + lastInitial).toUpperCase();
}

export function userFullName({ firstName, lastName }: CoachBasic | Profile | PlayerAccount): string {
  return (firstName ? firstName + " " : "") + (lastName ? lastName : "");
}

export function getFullName(firstName?: string, lastName?: string): string {
  return (firstName ? firstName + " " : "") + (lastName ? lastName : "");
}
export function validEmail(email: string): boolean {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
}

export const textWithLimit = (text: string | undefined, limit: number = 0): string => {
  const dots = "...";
  if (!text) {
    return "";
  }
  if (text && text.length > limit) {
    text = text.substring(0, limit) + dots;
  }

  return text;
};

export const getImageAsBase64 = (img: any, callback: any) => {
  const reader = new FileReader();
  reader.addEventListener("load", () => callback(reader.result));
  reader.readAsDataURL(img);
};

export function mediaElement(
  uri: string,
  extension: string,
  category: MediaCategory,
  id: number = -1,
  sortOrder: number = 0
): Media {
  return {
    category,
    extension,
    id,
    sortOrder,
    type: extension,
    uri,
  };
}

export function mediaElementSticket(
  uri: string,
  extension: string,
  category: MediaCategory,
  sortOrder: number = 0
): Media {
  return {
    id: sortOrder,
    category,
    extension,
    sortOrder,
    type: extension,
    uri,
  };
}

export function getReducedConfig(config: ConfigProperties): ConfigProperties {
  return {
    iOSUrl: config.iOSUrl,
    androidUrl: config.androidUrl,
    post: config.post,
    socialNetworks: {
      twitter: {
        api_key: config.socialNetworks.twitter.api_key,
        callback: config.socialNetworks.twitter.callback,
        tokenUrl: config.socialNetworks.twitter.tokenUrl,
      },
      facebook: {
        api_key: config.socialNetworks.facebook.api_key,
      },
      linkedin: {
        api_key: config.socialNetworks.linkedin.api_key,
        callback: config.socialNetworks.linkedin.callback,
        permissions: config.socialNetworks.linkedin.permissions,
      },
      instagram: {
        api_key: config.socialNetworks.instagram.api_key,
        callback: config.socialNetworks.instagram.callback,
        permissions: config.socialNetworks.instagram.permissions,
      },
      tiktok: {
        api_key: config.socialNetworks.tiktok.api_key,
        app_id: config.socialNetworks.tiktok.app_id,
        callback: config.socialNetworks.tiktok.callback,
        permissions: config.socialNetworks.tiktok.permissions,
      },
    },
    dashboardYoutubeId: config.dashboardYoutubeId,
    firebase: {
      ...config.firebase,
    },
    questions: config.questions,
    questionsLimits: config.questionsLimits,
    stripe: {
      public_key: config.stripe.public_key,
    },
    deployBaseUrl: config.deployBaseUrl,
    defaultComplianceDisclaimer: config.defaultComplianceDisclaimer,
    slugBaseUrl: config.slugBaseUrl,
    randomPrompts: {
      ...config.randomPrompts,
    },
    coachFacebookScopes: config.coachFacebookScopes,
    playerFacebookScopes: config.playerFacebookScopes,
    promptMaxLength: config.promptMaxLength,
    keywordMaxLength: config.keywordMaxLength,
    trialDays: config.trialDays,
    googleAnalyticsId: config.googleAnalyticsId,
    captchaSiteKey: config.captchaSiteKey,
  };
}

export function emptyField(value?: string): boolean {
  if (value === undefined) {
    return true;
  }
  return value.replace(/\s+/g, "").length === 0;
}

export function validateLong(minSize: number, maxSize: number, value?: string): boolean {
  if (value === undefined) {
    return false;
  }
  const valueWithOutSpaces = value.replace(/\s+/g, "");
  return valueWithOutSpaces.length >= minSize && valueWithOutSpaces.length <= maxSize;
}

export function removeWhiteSpace(value: string): string {
  return value.replace(/\s+/g, "");
}

export function getStatusText(status: IT.SubscriptionStatus | IT.CoachStatuses | IT.VideoProjectStatus | string) {
  const value = status.valueOf();
  if (value === "PENDING_FOR_APPROVAL") {
    return "PENDING";
  }
  return value.replace(/_/g, " ");
}

export function getSubscriptionType(subscriptionType?: IT.PricePlanTier) {
  if (subscriptionType === undefined) {
    return "";
  }

  switch (subscriptionType) {
    case IT.PricePlanTier.PLAYER_BASIC:
      return "Basic";
    case IT.PricePlanTier.PLAYER_PLUS:
      return "+";
  }
}

export function getLargeSubscriptionType(subscriptionType?: IT.PricePlanTier) {
  if (subscriptionType === undefined) {
    return "";
  }

  switch (subscriptionType) {
    case IT.PricePlanTier.PLAYER_BASIC:
      return "Basic";
    case IT.PricePlanTier.PLAYER_PLUS:
      return "SC+";
  }
}

/**
 * Returns MIME types that aren't "image/" or "video/"
 */
export function getValidFiles(files: File[]): File[] {
  return files.filter(file => {
    return file.type.startsWith("image/") || file.type.startsWith("video/");
  });
}

/**
 * Funtion to know if an image has the valid reels dimensions (Feed returns true always)
 * @param height
 * @param width
 * @param asReel
 * @returns true if the file has valid dimensions
 */
export function validDimensions(height: number, width: number, asReel: boolean = false): boolean {
  // Valid dimensions
  if (asReel) {
    return height >= 960 && height <= 1920 && width >= 540 && width <= 1080;
  } else {
    return true;
  }
}

export function validateMessageLenghtInSocialNetworks(
  message: string | undefined | null,
  postRestrictions: any
): string[] {
  let socialNetworks: string[] = [];
  const socialNetworksNames = ["Facebook", "Instagram", "LinkedIn", "Twitter", "Tiktok"];
  socialNetworksNames.forEach(sn => {
    if (message && message.length > postRestrictions[`${sn.toLocaleLowerCase()}_max_length`]) {
      socialNetworks.push(sn);
    }
  });
  return socialNetworks;
}

export function validAspectRatio(height: number, width: number, asReel: boolean = false): boolean {
  // Valid Aspect Ratios: 16:9 - 4:5, 1:1, 1.91:1, 9:16 and 3:4
  if (asReel) {
    return validReelsAspectRatio(height, width);
  } else {
    const validHorizontalWidth = Math.round((16 * height) / 9);
    const validVerticalWidth = Math.round((4 * height) / 5);
    const validHorizontalSquare = Math.round((1.91 * height) / width);
    const validTallVerticalWidth = Math.round((9 * height) / 16);
    const validMaxFeedVerticalWidth = Math.round((3 * height) / 4);

    if (
      validHorizontalWidth === width ||
      validVerticalWidth === width ||
      height === width ||
      validTallVerticalWidth === width ||
      validMaxFeedVerticalWidth === width ||
      validHorizontalSquare === 1
    ) {
      return true;
    }
    return false;
  }
}

function validReelsAspectRatio(height: number, width: number): boolean {
  // Valid Aspect Ratios: 9:16, 4:5
  const validVerticalWidth = Math.round((9 * height) / 16);
  const validVerticalWidth2 = Math.round((4 * height) / 5);
  if (validVerticalWidth === width || validVerticalWidth2 === width) {
    return true;
  }
  return false;
}

export function redirectPlayerIfSignUpFinished(signUpStep: number | undefined): void {
  if (signUpStep && signUpSteps[signUpStep] === PlayerSignUpStep.PLAYER_BUSINESS_DATA) {
    window.location.assign(URLS.player.dashboard);
  }
}

export function redirectPlayerIfSignUpNotFinished(userContext: AppUserContext | undefined): void {
  if (userContext?.signUpStep! !== PlayerSignUpStep.PLAYER_BUSINESS_DATA) {
    const url = getRedirectForPlayer(userContext!);
    window.location.assign(url);
  }
}

export function getRedirectForPlayer(userContext: AppUserContext): string {
  let finalUrl = URLS.player.dashboard;

  switch (userContext.signUpStep) {
    case PlayerSignUpStep.PLAYER_FINISH_SIGN_UP:
      finalUrl = URLS.player.firstLogin;
      break;
    case PlayerSignUpStep.PLAYER_PROFILE_DATA:
      finalUrl = URLS.player.firstSocialNetworks;
      break;
    default:
      finalUrl = URLS.player.dashboard;
      break;
  }

  return finalUrl;
}

export function validateEmail(email: string): any {
  return email.match(emailRegExp);
}

export function validatePendingAck(vp: IT.AkcElement[] | undefined, role: string): boolean {
  if (vp === undefined) {
    return false;
  }
  return vp.some(akc => akc.ackFlag === false && akc.userRole === role);
}
